import axios from 'axios'
import { logEvent, LogLevel } from '@praxis/component-logging'
import { getUiServicesEndpoint } from './utils'
import { BookingTypes, RawShipmentDetails, VendorId } from 'common/types'
import { isEmpty } from 'validate.js'

export interface ShipmentsByFiltersParams {
  bookingTypes: BookingTypes
  poIds?: string[]
  department?: string
  vendorIds?: VendorId[]
  shipmentIds?: string[]
  loadIds?: (string | number)[]
  truckLoadOptimizationRefIds?: string[]
  dateFrom?: string
  dateTo?: string
  bookingCategories?: string[]
  destinationLocCode?: string[]
  originLocCodes?: string[]
  returnAuthorizationNumbers?: string[]
  reverseLogisticsReferenceNumbers?: string[]
  currentPage?: number
  itemsPerPage?: number
}

export const getShipmentsByFilters = async (
  params: ShipmentsByFiltersParams,
): Promise<Record<string, any>> => {
  const {
    bookingTypes,
    poIds,
    department,
    vendorIds,
    shipmentIds,
    loadIds,
    truckLoadOptimizationRefIds,
    dateFrom,
    dateTo,
    bookingCategories,
    destinationLocCode,
    originLocCodes,
    returnAuthorizationNumbers,
    reverseLogisticsReferenceNumbers,
    currentPage = 1,
    itemsPerPage = 100,
  } = params

  const { key, url } = await getUiServicesEndpoint()
  let endpoint = `${url}/v1/search/shipments?key=${key}`
  if (!isEmpty(bookingTypes)) {
    endpoint += `&booking_types=${bookingTypes}`
  }
  if (!isEmpty(vendorIds)) {
    endpoint += `&vendor_ids=${vendorIds}`
  }
  if (!isEmpty(poIds)) {
    endpoint += `&po_numbers=${poIds}`
  }
  if (!isEmpty(department)) {
    endpoint += `&departments=${department}`
  }
  if (!isEmpty(shipmentIds)) {
    endpoint += `&tgt_booking_ids=${shipmentIds}`
  }

  if (!isEmpty(loadIds)) {
    endpoint += `&load_ids=${loadIds}`
  }

  if (!isEmpty(dateFrom)) {
    endpoint += `&date_from=${dateFrom}&date_to=${dateTo}`
  }
  if (!isEmpty(bookingCategories)) {
    endpoint += `&booking_categories=${bookingCategories}`
  }
  if (!isEmpty(destinationLocCode)) {
    endpoint += `&destination_location_codes=${destinationLocCode}`
  }
  if (!isEmpty(truckLoadOptimizationRefIds)) {
    endpoint += `&tlo_ref_ids=${truckLoadOptimizationRefIds}`
  }
  if (!isEmpty(originLocCodes)) {
    endpoint += `&origin_location_codes=${originLocCodes}`
  }
  if (!isEmpty(returnAuthorizationNumbers)) {
    endpoint += `&return_authorization_numbers=${returnAuthorizationNumbers}`
  }
  if (!isEmpty(reverseLogisticsReferenceNumbers)) {
    endpoint += `&reverse_logistics_reference_numbers=${reverseLogisticsReferenceNumbers}`
  }
  if (!isEmpty(currentPage)) {
    endpoint += `&page=${currentPage}`
  }
  if (!isEmpty(itemsPerPage)) {
    endpoint += `&per_page=${itemsPerPage}`
  }

  try {
    const response = await axios<{
      list: RawShipmentDetails[]
      pagination_metadata: Record<string, any>
    }>({
      method: 'get',
      url: endpoint,
    })
    return {
      ...(response.data ?? {}),
      http_status: response.status,
    }
  } catch (err: any) {
    const message = `Failed to load Shipments by filters`

    logEvent(
      {
        message: JSON.stringify({
          endpoint: endpoint,
          error: err?.response?.data,
          message: message,
          type: 'REQUEST_FAILURE',
          user: localStorage?.userInfo,
        }),
        url: window.location.href,
      },
      { level: LogLevel.Error },
    )

    throw new Error(message)
  }
}
